import { API_V1_AUTH } from '@/shared/config/api.constants';
import { apiRequest } from '@/shared/lib/api';
import { mapSessionUser } from '@/store/auth-store';

export interface LoginResponseRaw {
  user: Record<string, unknown>;
}

export async function authGetCsrf(): Promise<void> {
  await apiRequest<{ ok: boolean }>(API_V1_AUTH.csrf, { method: 'GET' });
}

/** Sent with org web login so the API records a web console session surface. */
export const SALAAMAH_CLIENT_HEADER = 'X-Salaamah-Client';
export const SALAAMAH_CLIENT_ADMIN_WEB = 'admin-web';

export async function authLogin(
  email: string,
  password: string,
  options?: { organizationId?: string },
): Promise<LoginResponseRaw> {
  return apiRequest<LoginResponseRaw>(API_V1_AUTH.login, {
    method: 'POST',
    body: {
      email,
      password,
      ...(options?.organizationId ? { organization_id: options.organizationId } : {}),
    },
    headers: { [SALAAMAH_CLIENT_HEADER]: SALAAMAH_CLIENT_ADMIN_WEB },
  });
}

export async function authSwitchOrganization(
  organizationId: string,
  signal?: AbortSignal,
): Promise<{ user: Record<string, unknown> }> {
  return apiRequest<{ user: Record<string, unknown> }>(API_V1_AUTH.switchOrganization, {
    method: 'POST',
    body: { organization_id: organizationId },
    signal,
    headers: { [SALAAMAH_CLIENT_HEADER]: SALAAMAH_CLIENT_ADMIN_WEB },
  });
}

export interface SignupPayload {
  full_name: string;
  email: string;
  password: string;
}

export interface SignupResponseRaw {
  organization_id: string;
  requires_email_verification: boolean;
}

export async function authSignup(body: SignupPayload): Promise<SignupResponseRaw> {
  return apiRequest<SignupResponseRaw>(API_V1_AUTH.signup, {
    method: 'POST',
    body,
  });
}

/**
 * Verification link states understood by the UI. Must mirror
 * `VERIFICATION_STATUS_*` in `apps/authentication/api/views.py`.
 * - `valid` — token matches an unverified org and hasn't expired; safe to POST
 *   the confirm request.
 * - `expired` — token was correct but has aged out; user needs a fresh email.
 * - `already_verified` — token is recognised but the org is already verified
 *   (a retry after success, or a second tab); show "already verified" copy.
 */
export type VerifyEmailStatus = 'valid' | 'expired' | 'already_verified';

/**
 * Inspect the verification token without mutating state.
 *
 * Safe to call on page load — the backend's `GET /verify-email/` is the
 * explicitly-idempotent counterpart of the confirm POST, precisely so that
 * email scanners, browser prefetchers and mail-client link previewers can
 * hit it without burning the token. Use this to decide which UI phase to
 * render (confirm-button / expired / already-verified / invalid).
 */
export async function authVerifyEmailCheck(
  token: string,
  signal?: AbortSignal,
): Promise<{ status: VerifyEmailStatus }> {
  const q = new URLSearchParams({ token });
  return apiRequest<{ status: VerifyEmailStatus }>(
    `${API_V1_AUTH.verifyEmail}?${q.toString()}`,
    { method: 'GET', signal },
  );
}

/**
 * Actually flip the organisation's `email_verified_at` flag. Only call this
 * in response to an explicit user click on the confirm button — never from a
 * `useEffect` on mount, or we'll re-introduce the very bug the GET/POST split
 * is designed to prevent.
 */
export async function authVerifyEmailConfirm(
  token: string,
  signal?: AbortSignal,
): Promise<{ organization_id: string }> {
  return apiRequest<{ organization_id: string }>(API_V1_AUTH.verifyEmail, {
    method: 'POST',
    body: { token },
    signal,
  });
}

export async function authMe(signal?: AbortSignal): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>(API_V1_AUTH.me, { method: 'GET', signal });
}

export async function authLogout(): Promise<void> {
  await apiRequest<null>(API_V1_AUTH.logout, { method: 'POST' });
}

export async function authResendVerification(): Promise<void> {
  await apiRequest<null>(API_V1_AUTH.resendVerification, { method: 'POST' });
}

export async function authDismissPostVerifyWelcome(): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>(API_V1_AUTH.dismissPostVerifyWelcome, {
    method: 'POST',
  });
}

export type GuardianPortalInviteContextStatus = 'valid' | 'expired' | 'invalid' | 'consumed';

export type GuardianPortalInviteContext =
  | { status: 'invalid' | 'expired' | 'consumed' }
  | {
      status: 'valid';
      invite: string;
      participant: { id: string; full_name: string };
      organization: { id: string; name: string };
      guardian_email: string;
    };

export async function authGuardianPortalInviteContext(
  invite: string,
  signal?: AbortSignal,
): Promise<GuardianPortalInviteContext> {
  const q = new URLSearchParams({ invite });
  return apiRequest<GuardianPortalInviteContext>(
    `${API_V1_AUTH.guardianPortalInviteContext}?${q.toString()}`,
    { method: 'GET', signal },
  );
}

export async function authGuardianPortalActivate(body: {
  invite: string;
  password: string;
  password_confirm: string;
}): Promise<Record<string, never>> {
  return apiRequest<Record<string, never>>(API_V1_AUTH.guardianPortalActivate, {
    method: 'POST',
    body,
  });
}

export type WorkforcePortalInviteContextStatus = 'valid' | 'expired' | 'invalid' | 'consumed';

export type WorkforcePortalInviteContext =
  | { status: 'invalid' | 'expired' | 'consumed' }
  | {
      status: 'valid';
      invite: string;
      staff: { id: string; full_name: string; email: string };
      organization: { id: string; name: string };
    };

export async function authWorkforcePortalInviteContext(
  invite: string,
  signal?: AbortSignal,
): Promise<WorkforcePortalInviteContext> {
  const q = new URLSearchParams({ invite });
  return apiRequest<WorkforcePortalInviteContext>(
    `${API_V1_AUTH.workforcePortalInviteContext}?${q.toString()}`,
    { method: 'GET', signal },
  );
}

export async function authWorkforcePortalActivate(body: {
  invite: string;
  password: string;
  password_confirm: string;
}): Promise<Record<string, never>> {
  return apiRequest<Record<string, never>>(API_V1_AUTH.workforcePortalActivate, {
    method: 'POST',
    body,
  });
}

export type WorkforcePasswordResetContext =
  | { status: 'invalid' | 'expired' }
  | {
      status: 'valid';
      reset: string;
      staff: { id: string; full_name: string; email: string };
      organization: { id: string; name: string };
    };

export async function authWorkforcePasswordResetContext(
  reset: string,
  signal?: AbortSignal,
): Promise<WorkforcePasswordResetContext> {
  const q = new URLSearchParams({ reset });
  return apiRequest<WorkforcePasswordResetContext>(
    `${API_V1_AUTH.workforcePasswordResetContext}?${q.toString()}`,
    { method: 'GET', signal },
  );
}

export async function authWorkforcePasswordResetComplete(body: {
  reset: string;
  password: string;
  password_confirm: string;
}): Promise<Record<string, never>> {
  return apiRequest<Record<string, never>>(API_V1_AUTH.workforcePasswordResetComplete, {
    method: 'POST',
    body,
  });
}

export function sessionUserFromLogin(raw: LoginResponseRaw) {
  return {
    user: mapSessionUser(raw.user),
  };
}
